/**
 * Per-category SEO overrides for the listing pages (`/search` and `/search/[slug]`).
 *
 * Source: "Non Meta Suggestions - buyme.lk" SEO audit (applied 2026-06). Keyed by the URL slug
 * so the value used by `generateMetadata` (server) and the `<h1>` in SearchPage (client) stay
 * in sync. Titles are full/absolute (they already include the brand) — feed them to Next's
 * `title.absolute` so the `%s | BuyMe` layout template is NOT appended again.
 *
 * Categories without an entry fall back to the generic template in `buildCategoryListingMetadata`.
 */
export interface ListingSeo {
  /** Full <title> — used verbatim (no `| BuyMe` suffix appended). */
  title: string;
  description: string;
  /** Visible page heading. Omit to fall back to the category name. */
  h1?: string;
  /** Optional SEO content-section heading rendered below the listings. */
  h2?: string;
  /** Body copy under the h2 (SEO content; safe to rewrite — drafted from the audit keywords). */
  seoBody?: string;
}

export const CATEGORY_LISTING_SEO: Record<string, ListingSeo> = {
  services: {
    title: 'Buy & Sell Services Online in Sri Lanka | BuyMe Classifieds',
    description:
      'Find and buy local services in Sri Lanka on BuyMe. Browse home, business & professional service listings from verified sellers. Post your free service ad today.',
    h1: 'Buy Services in Sri Lanka',
  },
  'fashion-beauty': {
    title: 'Buy Fashion & Beauty Products Online in Sri Lanka | BuyMe',
    description:
      'Shop fashion and beauty products online in Sri Lanka. Buy clothes, accessories & skincare at the best prices. Browse all top fashion beauty deals on BuyMe now.',
    h1: 'Buy Fashion & Beauty in Sri Lanka',
  },
  'hobby-sports-kids-items': {
    title: 'Buy Hobby, Sports & Kids Items for Sale Sri Lanka | BuyMe',
    description:
      "Shop hobby items, sports gear & kids' products for sale in Sri Lanka. Buy toys, fitness equipment & collectibles at the best deals on BuyMe online marketplace.",
    h1: 'Buy Hobby, Sports & Kids Items in Sri Lanka',
  },
  'business-industry-export-market': {
    title: 'Buy Business & Industry Products in Sri Lanka | BuyMe.lk',
    description:
      'Browse business, industrial & export market listings in Sri Lanka. Buy equipment, supplies & B2B products from verified sellers on BuyMe online marketplace.',
    h1: 'Buy Business & Industry Products in Sri Lanka',
  },
  'local-jobs-work-overseas': {
    title: 'Local Jobs & Overseas Work Listings in Sri Lanka | BuyMe',
    description:
      'Find local jobs and overseas work opportunities in Sri Lanka. Browse the latest job listings and career ads posted free on BuyMe Sri Lanka classified website.',
    h1: 'Jobs in Sri Lanka & Overseas Opportunities',
  },
  education: {
    title: 'Buy Education Services & Online Courses Sri Lanka | BuyMe',
    description:
      'Find education services, courses & tutoring listings in Sri Lanka. Browse and buy top educational opportunities from verified providers on BuyMe marketplace.',
    h1: 'Buy Education Services in Sri Lanka',
  },
  'kids-items': {
    title: 'Buy Kids Items in Sri Lanka | Toys, Clothes & Baby Gear',
    description:
      "Shop kids' items for sale in Sri Lanka — toys, children's clothing, baby gear & more. Find the best deals for kids from trusted sellers on BuyMe marketplace.",
    h1: 'Buy Kids Items in Sri Lanka',
  },
  'leisure-entertainment': {
    title: 'Buy Leisure & Entertainment Products in Sri Lanka | BuyMe',
    description:
      'Explore leisure and entertainment listings in Sri Lanka. Buy games, musical instruments, event tickets & hobby items at the best prices on BuyMe marketplace.',
    h1: 'Buy Leisure & Entertainment in Sri Lanka',
  },
  'free-stuff': {
    title: 'Free Stuff Sri Lanka | Browse Free Classified Ads | BuyMe',
    description:
      'Find free items near you in Sri Lanka. Browse free classified ads and grab free stuff from local sellers on BuyMe — post your free ad and give away items today.',
    h1: 'Free Stuff Listings in Sri Lanka',
  },
  'vehicles-sustainable-transport': {
    title: 'Buy Electric & Sustainable Vehicles in Sri Lanka | BuyMe',
    description:
      'Find sustainable transport and electric vehicles for sale in Sri Lanka. Buy EVs, hybrids & eco-friendly vehicles at the best prices on BuyMe Sri Lanka marketplace.',
    h1: 'Buy Sustainable Vehicles in Sri Lanka',
  },
  // electronics appears in both audits; the later "Meta Suggestion Report" supersedes the earlier one.
  electronics: {
    title: 'Electronics for Sale Sri Lanka | Phones, Laptops & More | BuyMe.lk',
    description:
      'Browse used & new electronics for sale in Sri Lanka. Find mobile phones, laptops, TVs & more from verified sellers. Get the best deals on BuyMe.lk. Post free today',
    // h1 omitted → falls back to the category name "Electronics" (audit: "convert the h2 into an h1").
    h2: 'Mobile Phones for Sale in Sri Lanka',
    seoBody:
      'Shop new and used mobile phones, laptops, TVs, and accessories from verified sellers across Sri Lanka. Compare the best prices and buy genuine electronics with secure deals, or list your own devices free to reach buyers island-wide.',
  },
  vehicle: {
    title: 'Used Vehicles for Sale in Sri Lanka | Cars, Bikes & More | BuyMe.lk',
    description:
      'Browse used vehicles for sale in Sri Lanka. Find cars, bikes, vans & more from verified sellers. Get the best deals on BuyMe.lk. Post your free ad today!',
    h2: 'Buy from Verified Vehicle Sellers in Sri Lanka',
    seoBody:
      'Find the latest used cars, bikes, vans, and three-wheelers for sale across Sri Lanka. Every listing comes from verified sellers, so you can compare prices and buy with confidence — or list your own vehicle free and reach thousands of local buyers.',
  },
  property: {
    title: 'Property for Sale in Sri Lanka | Houses, Land & Apartments | BuyMe.lk',
    description:
      'Browse properties for sale in Sri Lanka. Find houses, apartments, land & more from verified sellers. Get the best deals on BuyMe.lk. Post your free ad today!',
    h2: 'Houses for Sale in Sri Lanka',
    seoBody:
      'Browse houses, apartments, and land for sale or rent in Colombo and across Sri Lanka. Connect directly with verified sellers and agents to find your next home or investment, and post your property ad free to reach serious buyers island-wide.',
  },
  'home-garden': {
    title: 'Home & Garden Items for Sale in Sri Lanka | BuyMe.lk',
    description:
      'Browse home & garden items for sale in Sri Lanka. Find furniture, appliances, kitchen essentials & more from verified sellers. Get the best deals on BuyMe.lk. Post free!',
    h2: 'Home Appliances for Sale in Sri Lanka',
    seoBody:
      'Discover furniture, kitchen appliances, and garden essentials for sale in Sri Lanka. Buy quality home items from verified sellers at the best prices, and post your free ad to sell to local buyers fast.',
  },
  'fashion-jewellery': {
    title: 'Fashion Online Shopping Sri Lanka | Buy Clothes & Jewellery | BuyMe.lk',
    description:
      'Shop fashion & jewellery in Sri Lanka. Browse clothes, t-shirts, accessories & more from verified sellers. Find the best deals on BuyMe.lk. Post your free ad today!',
    h2: 'Buy Clothes Online in Sri Lanka',
    seoBody:
      'Shop clothes, t-shirts, accessories, and jewellery online in Sri Lanka. Browse the latest fashion from verified sellers and find great deals every day, or list your items free and start selling to buyers island-wide.',
  },
};

/** Base `/search` (all listings, no category) page. */
export const SEARCH_INDEX_SEO: ListingSeo = {
  title: 'Browse All Buy & Sell Classified Listings Sri Lanka | BuyMe',
  description:
    'Explore all buy and sell listings on BuyMe Sri Lanka. Find great deals on vehicles, property, electronics, fashion & more. Post your free classified ads today.',
  h1: 'Browse All Listings on BuyMe Sri Lanka',
};

export function getCategoryListingSeo(slug: string | null | undefined): ListingSeo | undefined {
  if (!slug) return undefined;
  return CATEGORY_LISTING_SEO[String(slug).trim().toLowerCase()];
}
